import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import {
  downloadCsv,
  escalateCancellations,
  getCancellations,
  TCancellationGroup,
} from '../../../requests/opsReports';
import {
  btnSecondary,
  Card,
  EmptyState,
  inputClass,
  labelClass,
  Pill,
  tableClass,
  tdClass,
  thClass,
  theadClass,
} from '../ui';

const WINDOWS = [7, 14, 30, 60, 90];

/**
 * Cancelled public registrations by account for a window, with the
 * accounts at or over the threshold flagged and one button to raise a
 * High-priority case on the account (T3.44). Counts of registrations;
 * nothing here scores anyone.
 */
const Cancellations: React.FC = () => {
  const qc = useQueryClient();
  const [days, setDays] = useState(30);
  const [escalateAt, setEscalateAt] = useState(3);
  const [busy, setBusy] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['opsCancellations', days, escalateAt],
    queryFn: () => getCancellations({ days, escalateAt }),
  });

  const escalate = useMutation({
    mutationFn: (g: TCancellationGroup) =>
      escalateCancellations({ accountId: g.accountId!, days, escalateAt }),
    onSuccess: (r) => {
      showToast(
        `Case ${r.caseNumber} raised on ${r.accountName ?? 'the account'}`,
        'success',
      );
      qc.invalidateQueries({ queryKey: ['opsCancellations'] });
    },
    onError: (e) => showToast(resolveApiMessage(e), 'error'),
  });

  const csv = async () => {
    setBusy(true);
    try {
      await downloadCsv(
        'ops/reports/cancellations',
        { days, escalateAt },
        `cancellations-${days}d.csv`,
      );
    } catch (e) {
      showToast(resolveApiMessage(e), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <div className={labelClass}>Window</div>
          <select
            className={inputClass}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {WINDOWS.map((d) => (
              <option key={d} value={d}>
                Last {d} days
              </option>
            ))}
          </select>
        </div>
        <div>
          <div className={labelClass}>Flag at</div>
          <input
            type="number"
            min={1}
            max={100}
            className={`${inputClass} w-24`}
            value={escalateAt}
            onChange={(e) =>
              setEscalateAt(Math.max(1, Number(e.target.value) || 1))
            }
          />
        </div>
        <span className="flex-1" />
        <button className={btnSecondary} onClick={csv} disabled={busy || !data}>
          {busy ? 'Preparing…' : 'Download CSV'}
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-500 bg-red-100 px-4 py-2 text-sm text-red-one">
          {resolveApiMessage(error)}
        </div>
      )}

      {data && (
        <div className="mb-4 grid grid-cols-3 gap-4">
          <Stat
            value={data.totals.registrations.toLocaleString()}
            label="cancelled public registrations"
          />
          <Stat
            value={data.totals.accounts.toLocaleString()}
            label="accounts"
          />
          <Stat
            value={data.totals.flagged.toLocaleString()}
            label={`flagged (${data.escalateAt}+ in the window)`}
            warn={data.totals.flagged > 0}
          />
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : data && data.items.length === 0 ? (
        <Card>
          <EmptyState
            title="No cancelled public registrations in this window"
            hint="Widen the window to look further back."
          />
        </Card>
      ) : (
        data && (
          <Card
            title="By account, most cancellations first"
            right={
              <span className="text-xs text-gray-three">
                {data.basis.registrations}
              </span>
            }
          >
            <div className="overflow-x-auto">
              <table className={`${tableClass} min-w-[1000px]`}>
                <thead className={theadClass}>
                  <tr>
                    <th className={thClass}>Account</th>
                    <th className={`${thClass} text-right`}>Cancelled</th>
                    <th className={`${thClass} text-right`}>Classes</th>
                    <th className={thClass}>Learners</th>
                    <th className={thClass}>Courses</th>
                    <th className={thClass}>Last</th>
                    <th className={thClass} />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((g) => (
                    <tr
                      key={g.accountId ?? 'none'}
                      className={g.flagged ? 'bg-orange-50' : ''}
                    >
                      <td className={`${tdClass} font-medium text-gray-four`}>
                        {g.accountId ? (
                          <Link
                            className="hover:text-blue-primary"
                            to={`${PATH.opsAccounts}/${g.accountId}`}
                          >
                            {g.accountName ?? g.accountId}
                          </Link>
                        ) : (
                          <span className="text-gray-three">No account</span>
                        )}
                        {g.flagged && (
                          <span className="ml-2">
                            <Pill value="flagged" tone="warn" />
                          </span>
                        )}
                      </td>
                      <td
                        className={`${tdClass} text-right font-semibold [font-variant-numeric:tabular-nums]`}
                      >
                        {g.count}
                      </td>
                      <td className={`${tdClass} text-right`}>{g.classes}</td>
                      <td className={`${tdClass} max-w-xs text-xs`}>
                        {g.learners.join(', ')}
                        {g.learnersOmitted > 0 && (
                          <span className="text-gray-three">
                            {' '}
                            +{g.learnersOmitted} more
                          </span>
                        )}
                      </td>
                      <td className={`${tdClass} max-w-xs text-xs`}>
                        {g.courses.join(', ')}
                      </td>
                      <td className={`${tdClass} whitespace-nowrap`}>
                        {g.lastDate?.slice(0, 10) ?? '—'}
                      </td>
                      <td className={`${tdClass} text-right`}>
                        {g.openCase ? (
                          <Link
                            className="text-xs text-blue-primary"
                            to={`${PATH.opsCases}?id=${g.openCase.id}`}
                          >
                            Case {g.openCase.caseNumber ?? ''} open
                          </Link>
                        ) : g.accountId ? (
                          <button
                            className={btnSecondary}
                            disabled={escalate.isPending}
                            onClick={() => escalate.mutate(g)}
                            title="Raise a High-priority Escalation case on the account listing these registrations"
                          >
                            Escalate
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-white-three px-5 py-3 text-xs text-gray-three">
              Escalate raises an Escalation case (Priority High, origin Portal)
              on the account with every registration listed, and is recorded
              with your name. One open escalation per account at a time.{' '}
              {data.basis.flagged}.
            </div>
          </Card>
        )
      )}
    </>
  );
};

const Stat: React.FC<{ value: string; label: string; warn?: boolean }> = ({
  value,
  label,
  warn,
}) => (
  <div
    className={`rounded-xl border bg-white px-5 py-4 ${
      warn ? 'border-orange-300' : 'border-white-regular'
    }`}
  >
    <div className="text-3xl font-bold text-gray-four [font-variant-numeric:tabular-nums]">
      {value}
    </div>
    <div className="mt-0.5 text-sm text-gray-three">{label}</div>
  </div>
);

export default Cancellations;
